import type { Category, ContextNode, Game } from "@/domain/types";
import { defaultProfile } from "./default";
import { edennilProfile } from "./edennil";

// Deadlock (Valve) PC bindings. Deadlock ships weekly and its defaults shift between major patches
// (Quick Buy moved off Shift+B in the May 2025 Shop Rework), so re-verify on major patches. No export
// file exists yet (a .cfg parser is deferred, domain.md), so both Profiles are hand-authored, each in
// its own file with its provenance: the Default from documented real defaults (./default), the Edennil
// setup from the maintainer's in-game keybind screenshots (./edennil). The design handoff's data was
// fiction (design-handoff-is-visual-source-not-data).
// Colours share one palette with Destiny 2 so the same kind of action reads the same hue in both
// games, with every category in a Match panel kept >= 60 degrees apart (no bleed): Abilities red (25),
// the fight category gold (combat/weapon 85), Items green (145), Interface cyan (205), Movement blue
// (265), Teammate Cameras magenta (325). The grey/null category (social here, Utility there) stays
// neutral. Spectator/Replay (purple 290) shows only in the Replay Context, never beside the cameras.
// Combat keeps its in-game name rather than Destiny's "Weapons"; camera is split from spectator so the
// in-match teammate cameras are not mislabelled as replay tools.
export const DEADLOCK_CATEGORIES: Record<string, Category> = {
  ability: { label: "Abilities", hue: 25 },
  item: { label: "Items", hue: 145 },
  movement: { label: "Movement", hue: 265 },
  combat: { label: "Combat", hue: 85 },
  social: { label: "Communication", hue: null },
  interface: { label: "Interface", hue: 205 },
  camera: { label: "Teammate Cameras", hue: 325 },
  spectator: { label: "Spectator / Replay", hue: 290 },
};

// Deadlock's Context axis: a flat two-Context split. "Match" holds live-game bindings;
// "Replay / Spectator" holds the in-game Spectator/Replay + Replay-Specific sections (DL-6). The
// resolver merges base under the selected overlay; base is empty here, so the two views are disjoint
// (no gameplay bleeds into the replay view). Fly up/down and Switch teams land on the same physical
// keys as Match's jump/crouch on a board-bound lens - active-code keying keeps them
// distinct (each projects onto the code its key emits through the FPS layer).
export const DEADLOCK_CONTEXTS: ContextNode[] = [
  { id: "match", label: "Match" },
  { id: "replay", label: "Replay / Spectator" },
];

export const deadlock: Game = {
  id: "deadlock",
  name: "Deadlock",
  categories: DEADLOCK_CATEGORIES,
  contexts: DEADLOCK_CONTEXTS,
  profiles: [defaultProfile, edennilProfile],
};
